import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreditAccount, CreditTransaction } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

const STATES = ['OPEN', 'PAID', 'REVERSED', ''] as const;
const TONE = { OPEN: 'amber', PAID: 'green', REVERSED: 'red' } as const;

/** Vista "Cartera": cuentas de crédito directo, abonos con su medio de pago y vencimientos (Etapa 2). */
export function Credits() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const places = user?.locations ?? [];
  const seesAll = !user?.role.scoped;

  const [status, setStatus] = useState<string>('OPEN');
  const [overdue, setOverdue] = useState(false);
  const [term, setTerm] = useState('');
  const [applied, setApplied] = useState('');
  const [location, setLocation] = useState<number | ''>('');
  useEffect(() => {
    const next = term.trim();
    if (next === applied) return undefined;
    const id = setTimeout(() => setApplied(next), 300);
    return () => clearTimeout(id);
  }, [term, applied]);

  const credits = useLoad(
    () => (user ? api.credits({ status: status || undefined, overdue, q: applied || undefined, location_id: location === '' ? undefined : Number(location), limit: 200 }) : Promise.resolve(null)),
    [user, status, overdue, applied, location],
  );
  const methods = useLoad(() => api.paymentMethods(), []);

  const [paying, setPaying] = useState<CreditAccount | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState<CreditAccount | null>(null);
  const [movements, setMovements] = useState<CreditTransaction[] | null>(null);
  const [reversing, setReversing] = useState<CreditTransaction | null>(null);
  const [reason, setReason] = useState('');
  const [dueFor, setDueFor] = useState<CreditAccount | null>(null);
  const [dueDate, setDueDate] = useState('');

  const rows = credits.data?.items ?? [];
  const totalBalance = credits.data?.balance ?? 0;
  const payable = (methods.data ?? []).filter((m) => m.active && m.kind !== 'DIRECT_CREDIT');
  const amountValue = Number(amount) || 0;
  const remaining = (paying?.balance ?? 0) - amountValue;

  const openHistory = (a: CreditAccount) => {
    setHistory(a);
    setMovements(null);
    api.creditTransactions(a.id).then(setMovements).catch(showError);
  };

  const submitPayment = (e: FormEvent) => {
    e.preventDefault();
    if (!paying || method === '') return;
    const scope = `cartera:abono:${paying.id}`;
    void run(async () => {
      try {
        const r = await api.payCredit(requestKey(scope), paying.id, { amount: amountValue, payment_method_id: Number(method), note });
        releaseKey(scope);
        setPaying(null); setAmount(''); setNote('');
        show(t(r.paid ? 'credit.settled' : 'credit.paid', { doc: r.doc_no, balance: formatMoney(r.balance, lang) }));
        await credits.reload();
      } catch (err) { showError(err); }
    });
  };

  const submitReverse = (e: FormEvent) => {
    e.preventDefault();
    if (!reversing?.document_id) return;
    const scope = `cartera:reverso:${reversing.document_id}`;
    void run(async () => {
      try {
        const r = await api.reverseDocument(requestKey(scope), reversing.document_id!, reason);
        releaseKey(scope);
        setReversing(null); setReason('');
        show(t('credit.reversed', { doc: r.doc_no }));
        await credits.reload();
        if (history) openHistory(history);
      } catch (err) { showError(err); }
    });
  };

  const submitDue = (e: FormEvent) => {
    e.preventDefault();
    if (!dueFor) return;
    const scope = `cartera:vencimiento:${dueFor.id}`;
    void run(async () => {
      try {
        await api.setDueDate(requestKey(scope), dueFor.id, dueDate.trim() === '' ? null : dueDate);
        releaseKey(scope);
        setDueFor(null); setDueDate('');
        show(t('credit.dueSaved'));
        await credits.reload();
      } catch (err) { showError(err); }
    });
  };

  return (
    <>
      <div className="actions" id="creditFilters" style={{ marginBottom: 12, alignItems: 'end', flexWrap: 'wrap' }}>
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor="crStatus">{t('credit.filterStatus')}</label>
          <select className="select" id="crStatus" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATES.map((s) => (
              <option key={s || 'all'} value={s}>{s ? t(`credit.status.${s}`) : t('credit.all')}</option>
            ))}
          </select>
        </div>
        {seesAll || places.length > 1 ? (
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="crLocation">{t('credit.location')}</label>
            <select className="select" id="crLocation" value={location} onChange={(e) => setLocation(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">{t('credit.allLocations')}</option>
              {places.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        ) : null}
        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="crSearch">{t('credit.search')}</label>
          <input className="input" id="crSearch" value={term} onChange={(e) => setTerm(e.target.value)} />
        </div>
        <label className="help" htmlFor="crOverdue" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8 }}>
          <input id="crOverdue" type="checkbox" checked={overdue} onChange={(e) => setOverdue(e.target.checked)} />
          {t('credit.overdue')}
        </label>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="lab">{t('credit.kpiBalance')}</div>
          <div className="val" id="crBalance">{formatMoney(totalBalance, lang)}</div>
          <div className="sub">{t('credit.kpiBalanceSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('credit.kpiAccounts')}</div>
          <div className="val" id="crCount">{formatNumber(credits.data?.total ?? 0, lang)}</div>
          <div className="sub">{t('credit.kpiAccountsSub')}</div>
        </div>
      </div>

      <Panel title={t('credit.title')} description={t('credit.desc')} badge={null}>
        {credits.data && rows.length === 0 ? <EmptyState icon="inbox" title={t('credit.none')} /> : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('credit.cols.customer')}</th>
                  <th>{t('credit.cols.sale')}</th>
                  {seesAll ? <th>{t('credit.cols.location')}</th> : null}
                  <th>{t('credit.cols.date')}</th>
                  <th className="num">{t('credit.cols.financed')}</th>
                  <th className="num">{t('credit.cols.paid')}</th>
                  <th className="num">{t('credit.cols.balance')}</th>
                  <th>{t('credit.cols.due')}</th>
                  <th>{t('common.status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="creditBody">
                {credits.loading && !credits.data ? <TableSkeleton cols={seesAll ? 10 : 9} rows={4} /> : null}
                {rows.map((a) => (
                  <tr key={a.id} data-credit={a.id} data-status={a.status}>
                    <td>
                      <b>{a.customer_name}</b>
                      <div className="tiny muted">{[a.customer_document, a.customer_phone].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td>{a.sale_no}</td>
                    {seesAll ? <td>{a.location_name}</td> : null}
                    <td>{formatDate(a.sold_at, lang, false)}</td>
                    <td className="num">{formatMoney(a.financed_amount, lang)}</td>
                    <td className="num" data-col="paid">{formatMoney(a.paid, lang)}</td>
                    <td className="num" data-col="balance"><b>{formatMoney(a.balance, lang)}</b></td>
                    <td data-col="due">
                      {a.due_date ? formatDate(`${a.due_date}T12:00:00`, lang, false) : <span className="tiny muted">{t('credit.noDue')}</span>}
                      {a.days_overdue > 0 ? <div className="tiny"><Badge tone="red">{t('credit.overdueDays', { n: a.days_overdue })}</Badge></div> : null}
                    </td>
                    <td><Badge tone={TONE[a.status]}>{t(`credit.status.${a.status}`)}</Badge></td>
                    <td>
                      <div className="actions">
                        {a.status === 'OPEN' && can('credit.collect') ? (
                          <button type="button" className="btn sm black" data-pay={a.id} onClick={() => { setPaying(a); setAmount(''); setNote(''); setMethod(payable[0]?.id ?? ''); }}>
                            {t('credit.pay')}
                          </button>
                        ) : null}
                        <button type="button" className="btn sm" data-history={a.id} onClick={() => openHistory(a)}>{t('credit.history')}</button>
                        {can('credit.manage') && a.status !== 'REVERSED' ? (
                          <button type="button" className="btn sm" data-due={a.id} onClick={() => { setDueFor(a); setDueDate(a.due_date ?? ''); }}>
                            {t('credit.editDue')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Modal open={!!paying} title={t('credit.payTitle', { customer: paying?.customer_name ?? '' })} help={t('credit.payHelp')} onClose={() => setPaying(null)}>
        <form className="formgrid" id="creditPayForm" onSubmit={submitPayment}>
          <div className="field">
            <label htmlFor="cpAmount">{t('credit.amount')}</label>
            <input id="cpAmount" type="number" min={1} max={paying?.balance ?? undefined} required value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cpMethod">{t('credit.method')}</label>
            <select id="cpMethod" required value={method} onChange={(e) => setMethod(e.target.value === '' ? '' : Number(e.target.value))}>
              {payable.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="field span2">
            <label htmlFor="cpNote">{t('credit.note')}</label>
            <input id="cpNote" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className={remaining < 0 ? 'paystatus bad span2' : 'paystatus span2'} id="cpPreview">
            {amountValue > 0 && remaining < 0
              ? t('credit.previewOver', { balance: formatMoney(paying?.balance ?? 0, lang) })
              : t('credit.preview', { amount: formatMoney(amountValue, lang), balance: formatMoney(Math.max(remaining, 0), lang) })}
          </div>
          <button className="btn black span2" type="submit" id="cpSubmit" disabled={busy || amountValue <= 0 || remaining < 0 || method === ''}>
            {t('credit.paySubmit')}
          </button>
        </form>
      </Modal>

      <Modal open={!!history} title={t('credit.historyTitle', { customer: history?.customer_name ?? '' })} help={t('credit.historyHelp')} onClose={() => setHistory(null)}>
        {movements && movements.length === 0 ? <EmptyState icon="inbox" title={t('credit.noMovements')} /> : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('credit.hcols.date')}</th>
                  <th>{t('credit.hcols.doc')}</th>
                  <th>{t('credit.hcols.kind')}</th>
                  <th className="num">{t('credit.hcols.amount')}</th>
                  <th>{t('credit.hcols.method')}</th>
                  <th>{t('credit.hcols.user')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="creditMovements">
                {!movements ? <TableSkeleton cols={7} rows={2} /> : null}
                {(movements ?? []).map((m) => (
                  <tr key={m.id} data-movement={m.doc_no ?? m.id} data-kind={m.kind}>
                    <td>{formatDate(m.created_at, lang)}</td>
                    <td>{m.doc_no ?? ''}</td>
                    <td>
                      {t(`credit.kind.${m.kind}`)}
                      {m.doc_status === 'REVERSED' ? <div className="tiny"><Badge tone="red">{t('credit.status.REVERSED')}</Badge></div> : null}
                    </td>
                    <td className="num">{formatMoney(m.amount, lang)}</td>
                    <td>{m.payment_method_name ?? ''}</td>
                    <td>{m.user_name ?? ''}</td>
                    <td>
                      {m.kind === 'PAYMENT' && m.doc_status !== 'REVERSED' && m.document_id && can('document.reverse') ? (
                        <button type="button" className="btn sm red" data-reverse={m.doc_no ?? ''} onClick={() => { setReversing(m); setReason(''); }}>
                          {t('credit.reverse')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal open={!!reversing} title={t('credit.reverseTitle', { doc: reversing?.doc_no ?? '' })} help={t('credit.reverseHelp')} onClose={() => setReversing(null)}>
        <form className="formgrid" id="creditReverseForm" onSubmit={submitReverse}>
          <div className="field span2">
            <label htmlFor="crReason">{t('credit.reason')}</label>
            <input id="crReason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button className="btn black span2" type="submit" disabled={busy || !reason.trim()}>{t('credit.reverse')}</button>
        </form>
      </Modal>

      <Modal open={!!dueFor} title={t('credit.dueTitle', { customer: dueFor?.customer_name ?? '' })} help={t('credit.dueHelp')} onClose={() => setDueFor(null)}>
        <form className="formgrid" id="creditDueForm" onSubmit={submitDue}>
          <div className="field span2">
            <label htmlFor="cdDate">{t('credit.dueDate')}</label>
            <input id="cdDate" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <button className="btn black span2" type="submit" id="cdSubmit" disabled={busy}>{t('common.save')}</button>
        </form>
      </Modal>
    </>
  );
}
